// ═════════════════════════════════════════════════════════════════════════════
// SNOMED CT concept search.
//
// This talks to the MAIN Django backend, not the NHCX coverage service — same
// endpoints and request body the doctor console uses (see
// doctor-console/Sections/Procedures.js and ChiefComplaint.js).
//
// snomed/search/ returns matching concepts. When it comes back empty the console
// falls back to snomed/suggest/, whose rows are looser *spelling* suggestions to
// search again with rather than concepts to pick — they carry no usable conceptId.
// That distinction matters here: coverage eligibility needs a real code, so the
// caller is told which of the two it got and offers them differently.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import { ICD10_CATALOG } from "./icd10-catalog";

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

// The doctor console caps the list at 300 — beyond that the dropdown is unusable
// and the payload is large. Same cap here.
const MAX_OPTIONS = 300;

const searchBody = (term, semanticTag) => ({
  term,
  state: "active",
  semantictag: semanticTag,
  acceptability: "synonyms",
  returnlimit: "100",
  refsetid: null,
  parentid: null,
  excludeparentconcept: false,
  fullconcept: true,
});

// Bearer is attached per call site in this codebase; APP-MODE / ORG-CODE are added
// by the global interceptor because these URLs are not on its exclusion list.
const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
});

const rowsOf = (res) => (res?.data?.status === "Success" ? res.data.data || [] : []);

/**
 * Search SNOMED concepts by name.
 *
 * Resolves `{ concepts, fromSuggest }`:
 *   fromSuggest false → concepts are real, each with a conceptId, safe to select.
 *   fromSuggest true  → no exact match; these are alternative search terms only,
 *                       so the UI should re-search with one rather than select it.
 */
export async function searchSnomedConcepts(term, semanticTag) {
  const headers = authHeaders();
  const body = searchBody(term, semanticTag);

  const found = rowsOf(await axios.post(`${BASE}snomed/search/`, body, { headers }));
  if (found.length) {
    return {
      concepts: found
        .slice(0, MAX_OPTIONS)
        .map((r) => ({ term: r.term, code: r.conceptId }))
        .filter((c) => c.term && c.code),
      fromSuggest: false,
    };
  }

  const suggested = rowsOf(await axios.post(`${BASE}snomed/suggest/`, body, { headers }));
  return {
    concepts: suggested
      .slice(0, MAX_OPTIONS)
      .map((r) => ({ term: r.term, code: "" }))
      .filter((c) => c.term),
    fromSuggest: suggested.length > 0,
  };
}

// C-DAC / Django wrappers vary the field names. additionalFields is the Snowstorm
// member shape. Catalog fills a missing display so we never send a code alone.
function pickIcdField(row, keys) {
  for (const k of keys) {
    const v = row?.[k] ?? row?.additionalFields?.[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return "";
}

function asMapRows(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.maps)) return data.maps;
  if (Array.isArray(data.mappedConcepts)) return data.mappedConcepts;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  return [data];
}

function normalizeIcdRow(row) {
  const code = pickIcdField(row, [
    "icd10_code",
    "icdCode",
    "icd_code",
    "mapTarget",
    "ICD10",
    "code",
  ]);
  if (!code) return null;
  if (!/^[A-Za-z][0-9]/.test(code)) return null;
  const display =
    pickIcdField(row, [
      "icd10_display",
      "icdTerm",
      "icd10_term",
      "icd_display",
      "mapTargetName",
      "ICD10_DESC",
      "display",
      "term",
    ]) ||
    ICD10_CATALOG.find((c) => c.code.toLowerCase() === code.toLowerCase())?.display ||
    code;
  return { code, display };
}

/**
 * Map a SNOMED CT concept to ICD-10 via the hospital SNOMED service
 * (`POST snomed/map_icd/`, C-DAC behind it).
 *
 * Resolves an array of `{ code, display }`. Empty when the concept has no map.
 * Throws when the C-DAC service itself is down so the UI can fall back to search.
 */
export async function mapSnomedToIcd10(conceptId) {
  const id = String(conceptId || "").trim();
  if (!id) return [];

  const res = await axios.post(
    `${BASE}snomed/map_icd/`,
    { id },
    { headers: authHeaders() }
  );

  if (res?.data?.status !== "Success") {
    const err = new Error(res?.data?.message || "ICD-10 map failed");
    err.unavailable = true;
    throw err;
  }

  const seen = new Set();
  return asMapRows(res.data.data)
    .map(normalizeIcdRow)
    .filter((row) => {
      if (!row || seen.has(row.code)) return false;
      seen.add(row.code);
      return true;
    });
}
